import { useState, useEffect, useRef } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const CountUp = ({ target, duration = 1800 }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting && !started.current) {
        started.current = true;
        let start = 0;
        const step = target / (duration / 16);
        const timer = setInterval(() => {
          start += step;
          if (start >= target) { setCount(target); clearInterval(timer); }
          else setCount(Math.floor(start));
        }, 16);
      }
    }, { threshold: 0.5 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [target, duration]);

  return <span ref={ref}>{count.toLocaleString()}</span>;
};

const StatsSection = () => {
  const [stats, setStats] = useState(null);
  const [visible, setVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.15 });
    if (sectionRef.current) obs.observe(sectionRef.current);
    axios.get(`${API}/stats`).then(r => setStats(r.data)).catch(() => {
      setStats({ total_cards: 14149, total_players: 500, total_expansions: 166, formats_supported: 6 });
    });
    return () => obs.disconnect();
  }, []);

  const items = [
    { label: "Cartas en Base de Datos", value: stats?.total_cards || 14149, icon: "🃏", color: "#d4af37", suffix: "+" },
    { label: "Duelistas Registrados", value: stats?.total_players || 0, icon: "⚔️", color: "#dc2626", suffix: "+" },
    { label: "Expansiones Yu-Gi-Oh!", value: stats?.total_expansions || 166, icon: "📦", color: "#7c3aed", suffix: "" },
    { label: "Formatos Soportados", value: stats?.formats_supported || 6, icon: "🏆", color: "#22c55e", suffix: "" },
  ];

  return (
    <section
      ref={sectionRef}
      className="relative py-12 overflow-hidden"
      style={{ background: 'linear-gradient(180deg,#050508,#080510,#050508)' }}
      data-testid="stats-section"
    >
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-0 right-0 h-px" style={{ background: 'linear-gradient(to right,transparent,rgba(220,38,38,0.5),transparent)' }} />
        <div className="absolute bottom-0 left-0 right-0 h-px" style={{ background: 'linear-gradient(to right,transparent,rgba(212,175,55,0.3),transparent)' }} />
      </div>

      <div className="container mx-auto px-6 md:px-12">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {items.map((item, i) => (
            <div
              key={i}
              className="relative p-5 md:p-6 rounded-2xl text-center group hover:scale-[1.03] transition-all duration-300"
              style={{
                background: `linear-gradient(135deg, rgba(10,5,15,0.9), rgba(15,5,10,0.9))`,
                border: `1px solid ${item.color}25`,
                boxShadow: `0 4px 20px rgba(0,0,0,0.4)`,
                opacity: visible ? 1 : 0,
                transform: visible ? 'translateY(0)' : 'translateY(30px)',
                transition: `all 0.6s cubic-bezier(0.16,1,0.3,1) ${i * 0.1}s`,
              }}
            >
              {/* Glow on hover */}
              <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                style={{ background: `radial-gradient(circle at center, ${item.color}10, transparent 70%)` }} />

              <div className="text-3xl mb-3">{item.icon}</div>
              <div
                className="text-3xl md:text-4xl font-black mb-1 leading-none"
                style={{ color: item.color, fontFamily: 'Cinzel, serif' }}
              >
                {stats ? <CountUp target={item.value} /> : '...'}{item.suffix}
              </div>
              <p className="text-gray-400 text-xs md:text-sm leading-tight">{item.label}</p>

              {/* Bottom accent line */}
              <div className="absolute bottom-0 left-1/2 -translate-x-1/2 h-0.5 rounded-full transition-all duration-300 group-hover:w-3/4 w-1/4"
                style={{ background: item.color }} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default StatsSection;
